import { toast } from 'react-toastify';
import { useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { useEntityStore } from '@ContractBuilder/store';
import { patchEntity } from '@mutations/patchEntity';
import { uploadAttachmentDocument } from '@mutations/uploadAttachmentDocument';
import { getAttachmentDocumentPresignedUrl } from '@queries/getAttachmentDocumentPresignedUrl';
import { ApiPatchOperation } from '@root/@types/types';
import { getFeatureFlag } from '@root/helpers/feature-flags/get-feature-flag';
import { useMutation } from '@tanstack/react-query';

interface RenameVars {
  attachmentId: string;
  attachmentName: string;
}

interface UpdateAttachmentStatusVars {
  attachmentId: string;
  attachmentStatus: 'pending' | 'ready';
}

interface CreateInlineAttachmentVars {
  attachment: File;
  attachmentName?: string;
  endorsementId?: string;
  isEndorsementView?: boolean;
  submissionId?: string;
  templateId?: string;
}

export function useAttachmentMutations() {
  const reloadSubmission = () => useEntityStore.getState().reloadSubmission();

  /**
   * Set an attachment's status, then ensure the contract PDF regenerates when it becomes ready.
   * When malware scanning runs, the post-scan version bump regenerates the PDF with the merged
   * file; only when scanning is disabled (the `MalwareScan` flag is off) must we bump the version
   * ourselves, via the same operation delete uses. Single source of this rule — shared by the
   * panel and inline upload paths.
   */
  const setAttachmentStatus = async (attachmentId: string, attachmentStatus: 'pending' | 'ready') => {
    await patchEntity({
      operation: ApiPatchOperation.UpdateResourceAttachmentStatus,
      attachmentId,
      attachmentStatus,
    });

    if (attachmentStatus === 'ready' && !getFeatureFlag('MalwareScan')) {
      await patchEntity({ operation: ApiPatchOperation.RecreatePdfWithAttachment });
    }
  };

  const renameAttachment = useMutation({
    mutationFn: ({ attachmentId, attachmentName }: RenameVars) =>
      patchEntity({
        operation: ApiPatchOperation.RenameResourceAttachment,
        attachmentId,
        attachmentName,
      }),
    onSuccess: reloadSubmission,
  });

  const updateAttachmentStatus = useMutation({
    mutationFn: ({ attachmentId, attachmentStatus }: UpdateAttachmentStatusVars) =>
      setAttachmentStatus(attachmentId, attachmentStatus),
    onSuccess: reloadSubmission,
  });

  const deleteAttachment = useMutation({
    mutationFn: async (attachmentId: string) => {
      await patchEntity({
        operation: ApiPatchOperation.RemoveResourceAttachment,
        attachmentId,
      });
      await patchEntity({
        operation: ApiPatchOperation.RecreatePdfWithAttachment,
      });
    },
    onSuccess: async () => {
      await reloadSubmission();
      toast.success('PDF removed successfully');
    },
  });

  const createInlineAttachment = useMutation({
    mutationFn: async ({
      attachment,
      attachmentName,
      endorsementId,
      isEndorsementView,
      submissionId,
      templateId,
    }: CreateInlineAttachmentVars) => {
      const formValues = useBlockEditFormStore.getState().formValues;

      const response = await getAttachmentDocumentPresignedUrl({
        fileName: attachment.name,
        name: attachmentName ?? '',
        size: attachment.size,
        section_id: formValues?.section_id,
        order: formValues?.order ?? -1,
        templateId,
        submissionId,
        endorsementId,
        block_type: isEndorsementView ? 'summary_block' : 'block',
      });

      if (response?.status !== 201) {
        throw new Error('Failed to upload');
      }

      const data = response.data.data ?? response.data;
      await uploadAttachmentDocument(data.url, { file: attachment }, data.fields);

      await setAttachmentStatus(data.id, 'ready');

      return data;
    },
    onSuccess: reloadSubmission,
  });

  return {
    renameAttachment,
    updateAttachmentStatus,
    deleteAttachment,
    createInlineAttachment,
  };
}
